import { useState } from "react";

/**
 * Custom hook for managing admin action handlers
 * Handles publish, discard, undo, and sign in/out operations
 */
export const useAdminHandlers = ({
  publishAllDrafts,
  discardDrafts,
  signOutUser,
  deleteVersion,
  closeAdminPanel,
  hasUnsavedChanges,
  signInWithGoogle,
}) => {
  const [publishingState, setPublishingState] = useState({});
  const [discardingState, setDiscardingState] = useState({});
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signInMessage, setSignInMessage] = useState(null);
  const [clearingCache, setClearingCache] = useState(false);

  // Handle publish single section
  const handlePublish = async (section, setIsVersionHistoryExpanded) => {
    if (!window.confirm(`Publish ${section.label} Section changes?`)) {
      return;
    }

    setPublishingState((prev) => ({ ...prev, [section.id]: true }));
    try {
      await section.publish();
      setPublishingState((prev) => ({ ...prev, [section.id]: "published" }));

      setTimeout(() => {
        setPublishingState((prev) => ({ ...prev, [section.id]: false }));
      }, 2000);
    } catch (error) {
      console.error("Publish error:", error);
      setPublishingState((prev) => ({ ...prev, [section.id]: false }));
      alert("Failed to publish changes");
    }
    setIsVersionHistoryExpanded(true);
  };

  // Handle discard single section
  const handleDiscard = (section) => {
    if (
      !window.confirm(
        `Discard all changes in ${section.label}? This cannot be undone.`
      )
    ) {
      return;
    }

    setDiscardingState((prev) => ({ ...prev, [section.id]: true }));
    setTimeout(() => {
      section.discard();
      setDiscardingState((prev) => ({ ...prev, [section.id]: "discarded" }));

      setTimeout(() => {
        setDiscardingState((prev) => ({ ...prev, [section.id]: false }));
      }, 1500);
    }, 300);
  };

  // Handle undo version restore
  const handleUndo = async (section, versionId, setIsChangesExpanded) => {
    if (
      !window.confirm(
        `Restore previous version? This will be saved as a draft.`
      )
    ) {
      return false; // User cancelled
    }

    try {
      await section.undo(versionId);
      setIsChangesExpanded(true);
      return true; // Operation succeeded
    } catch (error) {
      console.error("Error undoing version:", error);
      alert("Failed to restore version");
      return false; // Operation failed
    }
  };

  // Handle delete version
  const handleDeleteVersion = async (sectionId, versionId) => {
    if (!window.confirm("Delete this version permanently?")) {
      return false; // User cancelled
    }
    try {
      await deleteVersion(versionId);
      return true; // Operation succeeded
    } catch (error) {
      console.error("Error deleting version:", error);
      alert("Failed to delete version");
      return false; // Operation failed
    }
  };

  // Handle publish all sections
  const handlePublishAll = async (setIsVersionHistoryExpanded) => {
    if (
      !window.confirm(
        "Publish ALL changes? This will update all sections live."
      )
    ) {
      return;
    }

    setPublishingState((prev) => ({ ...prev, all: true }));

    try {
      await publishAllDrafts();
      setPublishingState((prev) => ({ ...prev, all: "published" }));

      setTimeout(() => {
        setPublishingState((prev) => ({ ...prev, all: false }));
      }, 2000);
    } catch (error) {
      console.error("Publish all error:", error);
      setPublishingState((prev) => ({ ...prev, all: false }));
      alert("Failed to publish all changes");
    }
    setIsVersionHistoryExpanded(true);
  };

  // Handle discard all sections
  const handleDiscardAll = () => {
    if (
      !window.confirm("Discard ALL unsaved changes? This cannot be undone.")
    ) {
      return;
    }

    setDiscardingState((prev) => ({ ...prev, all: true }));
    setTimeout(() => {
      discardDrafts();
      setDiscardingState((prev) => ({ ...prev, all: "discarded" }));

      setTimeout(() => {
        setDiscardingState((prev) => ({ ...prev, all: false }));
      }, 1500);
    }, 300);
  };

  // Handle sign in
  const handleSignIn = async () => {
    setIsSigningIn(true);
    setSignInMessage(null);

    try {
      const result = await signInWithGoogle();

      if (result.success) {
        setSignInMessage({
          type: "success",
          text: "Signed in successfully!",
        });

        setTimeout(() => setSignInMessage(null), 2000);
      }
    } catch (error) {
      console.error("Sign in error:", error);

      const errorMessage =
        error.displayMessage ||
        error.message ||
        "Sign in failed. Please try again.";

      setSignInMessage({
        type: "error",
        text: errorMessage,
        code: error.code,
      });

      setTimeout(() => setSignInMessage(null), 5000);
    } finally {
      setIsSigningIn(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    if (
      hasUnsavedChanges &&
      !window.confirm("You have unsaved changes. Sign out anyway?")
    ) {
      return;
    }
    try {
      await signOutUser();

      // Show success toast
      setSignInMessage({
        type: "success",
        text: "Signed out successfully!",
      });

      setTimeout(() => {
        setSignInMessage(null);
        closeAdminPanel();
      }, 2000);
    } catch (error) {
      console.error("Sign out error:", error);

      // Show error toast
      setSignInMessage({
        type: "error",
        text: "Sign out failed. Please try again.",
      });

      setTimeout(() => setSignInMessage(null), 3000);
    }
  };

  // Handle clear cache
  const handleClearCacheAndRefresh = async () => {
    setClearingCache(true);
    try {
      localStorage.removeItem("portfolio_local_db");
      window.location.reload();
    } catch (error) {
      console.error("Error clearing cache:", error);
      alert("Failed to clear cache. Please try again.");
    } finally {
      setClearingCache(false);
    }
  };

  return {
    publishingState,
    discardingState,
    isSigningIn,
    signInMessage,
    clearingCache,
    handlePublish,
    handleDiscard,
    handleUndo,
    handleDeleteVersion,
    handlePublishAll,
    handleDiscardAll,
    handleSignIn,
    handleSignOut,
    handleClearCacheAndRefresh,
    setSignInMessage,
  };
};
